import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { X } from "lucide-react";
import React from "react";
import toast from "react-hot-toast";

import { VisitsBulkEditFormView } from "./VisitsBulkEditFormView";
import { VisitsBulkEditPreviewView } from "./VisitsBulkEditPreviewView";
import type {
  CaseFileOption,
  MedChronTemplate,
  VisitDisplay,
  VisitFieldSuggestions,
} from "./types";
import type { BulkEditHeaderValues, BulkEditSaveValues } from "./types/bulkEdit";
import {
  type BulkEditPreview,
  buildBulkEditHeaderPrefill,
  buildBulkEditPreview,
  buildBulkVisitUpdatePayload,
  buildEffectiveBulkEditValues,
} from "./utils/bulkEditForm";
import { buildPageLimits, validateCitationDraftRows } from "./utils/visitCitations";
import { shortenFileName } from "./utils/visitFormatting";

type BulkEditStep = "form" | "preview";

export interface VisitsBulkEditModalProps {
  selectedVisitIds: string[];
  selectedVisitCount: number;
  visits: VisitDisplay[];
  template: MedChronTemplate | null;
  availableFiles: CaseFileOption[];
  fileMapping: Record<string, string>;
  fieldSuggestions: VisitFieldSuggestions;
  isSaving: boolean;
  onSave: (visitIds: string[], values: BulkEditSaveValues) => Promise<boolean>;
  onClose: () => void;
}

export const VisitsBulkEditModal: React.FC<VisitsBulkEditModalProps> = ({
  selectedVisitIds,
  selectedVisitCount,
  visits,
  template,
  availableFiles,
  fileMapping,
  fieldSuggestions,
  isSaving,
  onSave,
  onClose,
}) => {
  const [step, setStep] = React.useState<BulkEditStep>("form");
  const [preview, setPreview] = React.useState<BulkEditPreview | null>(null);
  const [prefill] = React.useState(() => buildBulkEditHeaderPrefill(selectedVisitIds, visits));
  const [values, setValues] = React.useState<BulkEditHeaderValues>(prefill);
  const [hasAttemptedReview, setHasAttemptedReview] = React.useState(false);

  // Prefilled citations stay valid even when their file is no longer in the case file list.
  const validFileIds = new Set([
    ...availableFiles.map((file) => file.file_id),
    ...prefill.citations.map((row) => row.file_id),
  ]);
  const citationValidation = validateCitationDraftRows(
    values.citations,
    validFileIds,
    buildPageLimits(availableFiles),
    new Set(prefill.citations)
  );
  const saveValues: BulkEditSaveValues = { ...values, citations: citationValidation.citations };

  const handleReview = () => {
    if (!citationValidation.isValid) {
      setHasAttemptedReview(true);
      toast.error("Check the citations");
      return;
    }
    if (!buildBulkVisitUpdatePayload(saveValues)) {
      toast.error("Enter at least one field to update");
      return;
    }

    const nextPreview = buildBulkEditPreview(
      selectedVisitIds,
      visits,
      saveValues,
      template,
      fileMapping,
      shortenFileName
    );
    if (!nextPreview) {
      toast.error("No changes to apply");
      return;
    }

    setPreview(nextPreview);
    setStep("preview");
  };

  const handleConfirm = async () => {
    const effectiveValues = buildEffectiveBulkEditValues(selectedVisitIds, visits, saveValues);
    await onSave(selectedVisitIds, effectiveValues);
  };

  const title = step === "form" ? "Bulk edit selected visits" : "Review bulk changes";
  const subtitle =
    step === "form"
      ? `Apply header changes to ${selectedVisitCount} visit${selectedVisitCount !== 1 ? "s" : ""}.`
      : null;

  const handleOpenChange = (next: boolean) => {
    if (isSaving) return;
    if (!next) onClose();
  };

  return (
    <Dialog open onOpenChange={handleOpenChange}>
      <DialogContent
        size="xl"
        hideCloseButton
        className="flex max-h-[95vh] flex-col gap-0 overflow-hidden p-0"
        onInteractOutside={(e) => {
          if (isSaving) e.preventDefault();
        }}
        onEscapeKeyDown={(e) => {
          if (isSaving) e.preventDefault();
        }}
      >
        <DialogHeader className="flex shrink-0 flex-row items-start justify-between space-y-0 border-b border-neutral-100 px-6 py-4 text-left">
          <div>
            <DialogTitle className="text-xl font-bold text-neutral-900">{title}</DialogTitle>
            {subtitle && (
              <DialogDescription className="mt-1 text-sm text-neutral-500">
                {subtitle}
              </DialogDescription>
            )}
          </div>
          <DialogClose
            type="button"
            disabled={isSaving}
            className="rounded-full p-2 text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-600 disabled:opacity-50"
            aria-label="Close dialog"
          >
            <X size={20} />
          </DialogClose>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {step === "form" ? (
            <VisitsBulkEditFormView
              values={values}
              onChange={setValues}
              citationRowErrors={hasAttemptedReview ? citationValidation.rowErrors : []}
              template={template}
              availableFiles={availableFiles}
              fileMapping={fileMapping}
              fieldSuggestions={fieldSuggestions}
              isSaving={isSaving}
            />
          ) : (
            preview && <VisitsBulkEditPreviewView preview={preview} />
          )}
        </div>

        <div className="shrink-0 border-t border-neutral-100 bg-card px-6 py-4">
          {step === "form" ? (
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                disabled={isSaving}
                className="rounded-md border border-neutral-300 bg-card px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleReview}
                disabled={isSaving}
                className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-hover disabled:opacity-50"
              >
                Review changes
              </button>
            </div>
          ) : (
            preview && (
              <div className="flex items-center justify-between gap-3">
                <button
                  type="button"
                  onClick={() => setStep("form")}
                  disabled={isSaving}
                  className="rounded-md border border-neutral-300 bg-card px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50 disabled:opacity-50"
                >
                  Back
                </button>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={onClose}
                    disabled={isSaving}
                    className="rounded-md border border-neutral-300 bg-card px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleConfirm}
                    disabled={isSaving}
                    className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-hover disabled:opacity-50"
                  >
                    {isSaving
                      ? "Updating…"
                      : `Apply changes to ${preview.visitCount} visit${preview.visitCount !== 1 ? "s" : ""}`}
                  </button>
                </div>
              </div>
            )
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
